/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable react/require-default-props */
import React, { useRef, useState, useMemo } from 'react';
import { translate } from '@utils/i18n';
import { Popup, PopupSection, PopupActionList, PopupSearchInput } from '../../../Popup';
import { Button } from '../../../../atoms/Button/Button';
import './InlineSelectStyles.scss';
import { InlineEditDropdown, INLINE_EDIT_POPUP_COLLISION } from '../shared/InlineEditDropdown';
import { INLINE_EDIT_NAMESPACE } from '../shared/constants';
/**
 * InlineSelect Component
 * Click-to-edit inline select input with popup dropdown
 * @version 1.0.0
 * @since 2026-01-27
 */
/**
 * Default equality function using reference equality
 */
const defaultIsEqual = (a, b) => a === b;
export function InlineSelect({ id, className, optionRenderer, valueRenderer, options, label, value, onChange, showSearch = false, searchPlaceholder, maxHeight, disabled = false, isEqual = defaultIsEqual, multiple = false, values, onMultiChange, displayMode = 'labels', footerAction }) {
    var _a;
    // Translation keys for user-visible strings
    const t = {
        searchPlaceholder: searchPlaceholder || translate('search_placeholder', { namespace: INLINE_EDIT_NAMESPACE }),
        noOptionsFound: translate('no_options_found', { namespace: INLINE_EDIT_NAMESPACE })
    };
    const [visible, setVisible] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const anchor = useRef(null);
    // Filter options based on search term
    const filteredOptions = useMemo(() => {
        if (!showSearch || !searchTerm.trim()) {
            return options;
        }
        const searchLower = searchTerm.toLowerCase().trim();
        return options.filter((option) => {
            // Use searchKey if provided (preferred for JSX/complex labels)
            // Otherwise, only search string labels (non-string labels return empty string)
            const searchableText = option.searchKey || (typeof option.label === 'string' ? option.label : '');
            return searchableText.toLowerCase().trim().includes(searchLower);
        });
    }, [options, searchTerm, showSearch]);
    const handleOpen = () => {
        setVisible(true);
        setSearchTerm('');
    };
    const handleClose = () => {
        setVisible(false);
        setSearchTerm('');
    };
    // Helper function for custom renderers to perform selection and close menu
    const handleSelect = (selectedValue) => {
        onChange === null || onChange === void 0 ? void 0 : onChange(selectedValue);
        handleClose();
    };
    const getMultiDisplayValue = () => {
        const currentValues = values !== null && values !== void 0 ? values : [];
        const selectedOptions = options.filter((opt) => currentValues.some((v) => isEqual(opt.value, v)));
        if (selectedOptions.length === 0)
            return null;
        if (displayMode === 'count') {
            return `${selectedOptions.length} selected`;
        }
        if (displayMode === 'summary' && selectedOptions.length > 1) {
            return (React.createElement(React.Fragment, null,
                selectedOptions[0].label,
                " +",
                selectedOptions.length - 1,
                " more"));
        }
        if (selectedOptions.every((opt) => typeof opt.label === 'string')) {
            return selectedOptions.map((opt) => opt.label).join(', ');
        }
        return selectedOptions.map((opt, idx) => {
            var _a;
            return (React.createElement(React.Fragment, { key: (_a = opt.id) !== null && _a !== void 0 ? _a : `sel-${idx}` },
                idx > 0 && ', ',
                opt.label));
        });
    };
    return (React.createElement("div", { className: className ? `${className} qmb-ui-inline-select` : 'qmb-ui-inline-select' },
        React.createElement(InlineEditDropdown, { onClick: handleOpen, refComp: anchor, disableEdit: disabled, "aria-haspopup": "listbox", "aria-expanded": visible, "aria-controls": id },
            label ? React.createElement(React.Fragment, null,
                label,
                ": ") : '',
            React.createElement("span", { className: "selectValue" }, multiple
                ? getMultiDisplayValue()
                : (value !== undefined ? valueRenderer === null || valueRenderer === void 0 ? void 0 : valueRenderer(value, handleSelect) : null) ||
                    ((_a = options.find((option) => value !== undefined && isEqual(option.value, value))) === null || _a === void 0 ? void 0 : _a.label))),
        React.createElement(Popup, { position: "bottom-left", anchor: anchor.current, show: visible, onClose: handleClose, collision: INLINE_EDIT_POPUP_COLLISION, containerClass: "qmb-ui-popup--action-list qmb-ui-inline-select", containerStyle: maxHeight ? { maxHeight: `${maxHeight}px` } : {} },
            showSearch && (React.createElement(PopupSearchInput, { value: searchTerm, onChange: setSearchTerm, placeholder: t.searchPlaceholder })),
            React.createElement(PopupSection, null,
                React.createElement(PopupActionList, null,
                    filteredOptions.map((option, index) => {
                        var _a;
                        const optionKey = (_a = option.id) !== null && _a !== void 0 ? _a : `option-${index}`;
                        if (multiple) {
                            const isSelected = (values !== null && values !== void 0 ? values : []).some((v) => isEqual(v, option.value));
                            return (React.createElement(Button, { key: optionKey, variant: "default", "aria-selected": isSelected, onClick: () => {
                                    const currentValues = values !== null && values !== void 0 ? values : [];
                                    const newValues = isSelected
                                        ? currentValues.filter((v) => !isEqual(v, option.value))
                                        : [...currentValues, option.value];
                                    onMultiChange === null || onMultiChange === void 0 ? void 0 : onMultiChange(newValues);
                                } },
                                option.label,
                                isSelected && React.createElement("i", { className: "fa-light fa-check" })));
                        }
                        return ((optionRenderer === null || optionRenderer === void 0 ? void 0 : optionRenderer(option, handleSelect)) || (React.createElement(Button, { key: optionKey, variant: "default", "aria-selected": value !== undefined && isEqual(option.value, value), onClick: () => {
                                onChange === null || onChange === void 0 ? void 0 : onChange(option.value);
                                handleClose();
                            } }, option.label)));
                    }),
                    filteredOptions.length === 0 && showSearch && searchTerm.trim() && (React.createElement("span", { style: { padding: '8px 12px', color: '#666', fontStyle: 'italic' } }, t.noOptionsFound)))),
            footerAction && (React.createElement("div", { className: "qmb-ui-inline-select__footer" },
                React.createElement(Button, { type: "button", variant: "highlighted", onClick: () => {
                        handleClose();
                        footerAction.onSelect();
                    } }, footerAction.label))))));
}
